import { lazy, type ComponentType } from 'react'

/**
 * A wrapper around React.lazy that automatically reloads the page
 * if fetching the chunk fails (usually due to a version mismatch deployment).
 * 
 * @param componentImport The dynamic import function (e.g. () => import('./MyComponent'))
 * @returns A lazy loaded component
 */
export const lazyWithRetry = <T extends ComponentType<any>>(
    componentImport: () => Promise<{ default: T }>
) => {
    return lazy(async () => {
        try {
            const start = componentImport()
            // Wait for the promise to resolve or reject
            return await start
        } catch (error: any) {
            // Check if we've already tried to force refresh the page
            const pageHasAlreadyBeenForceRefreshed = JSON.parse(
                window.sessionStorage.getItem('page-has-been-force-refreshed') || 'false'
            )

            // Assuming "ChunkLoadError" or similar network error for modules
            // But usually just checking strict inequality is enough if we trust the retry logic
            // Note: React 18+ might re-throw the error if we don't handle it precisely,
            // but here we are catching the import() promise rejection.

            if (!pageHasAlreadyBeenForceRefreshed) {
                // Mark that we are attempting a refresh
                window.sessionStorage.setItem('page-has-been-force-refreshed', 'true')
                // Reload the page to fetch fresh main resources (and hopefully new hashed chunks)
                window.location.reload()

                // Return a never-resolving promise to keep Suspense in pending state while reloading
                // or just throw to let ErrorBoundary catch if reload is slow?
                // Actually reloading will unload the page.
                // But to satisfy types, we can throw or return a placeholder.
                // Throwing is safer so if reload fails for some reason, ErrorBoundary sees it.
                throw error
            } else {
                // We already tried reloading and it failed again. 
                // Likely a persistent network error or a code error, not just a version mismatch.
                // Throw the error so the ErrorBoundary triggers.
                throw error
            }
        }
    })
}
